/**
 * 報修單更正紀錄（GET `/{id}/corrections`）的 before／after 轉成顯示列。
 * 後端依操作寫不同形狀：
 * - 一般更正：`{ header: {...}, lines: [{ id, ... }] }`
 * - 更正 SN：平面欄位（serialNo、productCode、itemName…）
 * - 新增品項：after `{ operation: 'ADD_LINE', ... }`、before `{}`
 * - 移除品項：before `{ operation: 'REMOVE_LINE', ... }`、after `{ removed: true }`
 */

export const CORRECTION_FIELD_LABELS = {
  repairRequestDate: '報修日',
  receivedDate: '收件日',
  customerName: '客戶名稱',
  contactName: '聯絡人',
  phone: '電話',
  fax: '傳真',
  taxId: '統一編號',
  address: '地址',
  customerId: '客戶',
  remark: '備註',
  serialNo: '產品序號',
  productCode: '產品代號',
  itemName: '品名',
  problemDescription: '問題描述',
  accessories: '配件',
  version: '版本',
  salesDate: '銷售日',
  warrantyExpiryDate: '保固迄日',
};

/** 內部 id／旗標不顯示。 */
const HIDDEN_KEYS = new Set([
  'id',
  'operation',
  'lineId',
  'itemId',
  'materialSnId',
  'mtrSnId',
  'publicUnregisteredRepair',
  'removed',
  'lineNo',
]);

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function displayValue(value) {
  if (value == null || value === '') {
    return '—';
  }
  return String(value);
}

function flatRows(before, after, prefix = '') {
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])]
    .filter((key) => !HIDDEN_KEYS.has(key));
  return keys.map((key) => ({
    label: `${prefix}${CORRECTION_FIELD_LABELS[key] ?? key}`,
    before: displayValue(before?.[key]),
    after: displayValue(after?.[key]),
  }));
}

/**
 * @param {{ before?: object, after?: object }} entry 已 camel
 * @param {Record<string, string>} [serialByLineId] 目前 detail 的 lineId → SN，用來標示品項
 * @returns {{ kind: string, rows: { label: string, before: string, after: string }[] }}
 */
export function summarizeCorrectionEntry(entry, serialByLineId = {}) {
  const before = isPlainObject(entry?.before) ? entry.before : {};
  const after = isPlainObject(entry?.after) ? entry.after : {};

  if (after.operation === 'ADD_LINE') {
    return {
      kind: '新增品項',
      rows: flatRows({}, after),
    };
  }

  if (before.operation === 'REMOVE_LINE') {
    return {
      kind: '移除品項',
      rows: flatRows(before, {}).map((row) => ({ ...row, after: '已移除' })),
    };
  }

  if (isPlainObject(before.header) || isPlainObject(after.header)
    || Array.isArray(before.lines) || Array.isArray(after.lines)) {
    const rows = flatRows(before.header, after.header);
    const beforeLines = Array.isArray(before.lines) ? before.lines : [];
    const afterLines = Array.isArray(after.lines) ? after.lines : [];
    const lineIds = [...new Set([...beforeLines, ...afterLines].map((line) => line?.id).filter(Boolean))];
    lineIds.forEach((lineId, index) => {
      const oldLine = beforeLines.find((line) => line?.id === lineId) ?? {};
      const newLine = afterLines.find((line) => line?.id === lineId) ?? {};
      const tag = serialByLineId[lineId] ?? oldLine.serialNo ?? `品項 ${index + 1}`;
      rows.push(...flatRows(oldLine, newLine, `${tag}・`));
    });
    return { kind: '更正資料', rows };
  }

  return {
    kind: Object.prototype.hasOwnProperty.call(after, 'serialNo') ? '更正序號' : '更正',
    rows: flatRows(before, after),
  };
}
